/**
 * Naming a group of legs — ROADMAP 9.26.
 *
 * One field and two buttons, opened from the manual block and reused for
 * renaming a basket afterwards. The two are the same question — "what do
 * you call this?" — and a second dialog for the second occasion would be
 * two places to change the bound on the name.
 */

import { useEffect, useRef, useState } from 'react'
import { Layers } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/** Matches `TRADE_LABEL_MAX` on the server. */
export const TRADE_LABEL_MAX = 48

export function NameGroupDialog({
  title,
  initial = '',
  legCount,
  busy,
  error,
  warning,
  onSubmit,
  onClose,
}: {
  title: string
  initial?: string
  /** Absent when renaming — the count is only news while grouping. */
  legCount?: number
  busy: boolean
  error: string | null
  /** Said before the act, not discovered after it. Never blocks. */
  warning?: string | null
  onSubmit: (label: string) => void
  onClose: () => void
}) {
  const [label, setLabel] = useState(initial)
  const box = useRef<HTMLInputElement>(null)

  // The trader opened this to type. Focus and select, so renaming is one
  // gesture rather than a click, a select-all and then the typing.
  useEffect(() => {
    box.current?.focus()
    box.current?.select()
  }, [])

  const submit = () => {
    const trimmed = label.trim()
    if (trimmed === '' || busy) return
    onSubmit(trimmed)
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="qbar-card w-full max-w-[380px] overflow-hidden rounded-[12px]"
        data-testid="name-group-dialog"
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <Layers size={14} className="text-primary" />
          <h2 className="text-[13.5px] font-semibold">{title}</h2>
          {legCount !== undefined && (
            <span className="num ml-auto text-[11px] text-muted-foreground">
              {legCount} legs
            </span>
          )}
        </div>

        <div className="px-4 py-3.5">
          <Input
            ref={box}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submit()
              }
              if (e.key === 'Escape') onClose()
            }}
            maxLength={TRADE_LABEL_MAX}
            placeholder="Monday fly"
            aria-label="Name for this group"
            data-testid="group-label-input"
          />
          {/*
            A WARNING, NOT A REFUSAL. A calendar spread is a real structure
            a trader may well want grouped; what they must not do is meet
            its consequence on the drawer afterwards and read it as a fault.
          */}
          {warning && !error && (
            <p className="mt-2 text-[11.5px] leading-[1.5] text-warning" data-testid="group-warning">
              {warning}
            </p>
          )}
          {error && (
            <p className="mt-2 text-[11.5px] text-destructive" data-testid="group-error">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-2.5">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={submit}
            disabled={busy || label.trim() === ''}
            data-testid="group-confirm"
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  )
}
